/**
 * Centralized date display utilities for Viveka marketplace (FTW).
 *
 * CONVENTION:
 *   - All dates should arrive from the API as ISO 8601 UTC strings
 *   - Legacy Firestore data may use epoch milliseconds (number)
 *   - Display formatting is locale-aware using Intl.DateTimeFormat
 *   - User timezone is applied at the display layer only
 *   - This module has ZERO dependencies (no moment.js)
 *
 * MIGRATION NOTE:
 *   Replace all scattered moment().format() calls with these functions.
 *   See: Viveka Date Standardization Migration Plan, Phase 3.
 */

/** @typedef {'us' | 'eu' | 'iso' | 'legal'} DateStyle */

/**
 * Normalize any date-like value to a Date object.
 * Accepts: ISO string, epoch ms (number), or Date object.
 * Returns null for invalid/empty input.
 *
 * @param {string|number|Date|null|undefined} value
 * @returns {Date|null}
 */
function toDate(value) {
  if (!value && value !== 0) return null;

  let d;
  if (value instanceof Date) {
    d = value;
  } else if (typeof value === 'number') {
    // Epoch milliseconds from Firestore (e.g. new Date().getTime())
    d = new Date(value);
  } else if (typeof value === 'string') {
    d = new Date(value);
  } else {
    return null;
  }

  return isNaN(d.getTime()) ? null : d;
}

/**
 * Get the user's browser timezone as an IANA string.
 * @returns {string}
 */
function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'UTC';
  }
}

/**
 * Format a date for display (date only, no time).
 *
 * @param {string|number|Date|null|undefined} value - ISO string, epoch ms, or Date
 * @param {DateStyle} [style='us'] - Display style
 * @param {string} [userTimezone] - IANA timezone (defaults to browser)
 * @returns {string} Formatted date string, or '' if input is invalid
 *
 * @example
 *   formatDateDisplay('2026-03-23T14:00:00.000Z', 'us')    // "Mar 23, 2026"
 *   formatDateDisplay('2026-03-23T14:00:00.000Z', 'eu')    // "23 Mar 2026"
 *   formatDateDisplay('2026-03-23T14:00:00.000Z', 'iso')   // "2026-03-23"
 *   formatDateDisplay('2026-03-23T14:00:00.000Z', 'legal') // "March 23, 2026"
 *   formatDateDisplay(1711195200000, 'us')                  // "Mar 23, 2024"
 */
export function formatDateDisplay(value, style, userTimezone) {
  if (style === undefined) style = 'us';
  if (userTimezone === undefined) userTimezone = browserTimezone();

  const date = toDate(value);
  if (!date) return '';

  switch (style) {
    case 'us':
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: userTimezone,
      }).format(date);

    case 'eu':
      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: userTimezone,
      }).format(date);

    case 'iso':
      return date.toISOString().slice(0, 10);

    case 'legal':
      return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: userTimezone,
      }).format(date);

    default:
      return date.toISOString().slice(0, 10);
  }
}

/**
 * Format a date with time for display.
 *
 * @param {string|number|Date|null|undefined} value - ISO string, epoch ms, or Date
 * @param {DateStyle} [style='us'] - Display style
 * @param {string} [userTimezone] - IANA timezone (defaults to browser)
 * @returns {string} Formatted date+time string, or '' if input is invalid
 *
 * @example
 *   formatDateTimeDisplay('2026-03-23T14:00:00.000Z', 'us', 'America/New_York')
 *   // "Mar 23, 2026, 10:00 AM"
 *
 *   formatDateTimeDisplay('2026-03-23T14:00:00.000Z', 'eu')
 *   // "23 Mar 2026, 14:00"
 *
 *   formatDateTimeDisplay(1711195200000, 'us')
 *   // "Mar 23, 2024, 2:00 PM"
 */
export function formatDateTimeDisplay(value, style, userTimezone) {
  if (style === undefined) style = 'us';
  if (userTimezone === undefined) userTimezone = browserTimezone();

  const date = toDate(value);
  if (!date) return '';

  const locale = style === 'eu' ? 'en-GB' : 'en-US';

  const opts = {
    timeZone: userTimezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: style !== 'eu',
  };

  if (style === 'iso') {
    return (
      date.toISOString().slice(0, 10) +
      ' ' +
      new Intl.DateTimeFormat(locale, opts).format(date)
    );
  }

  if (style === 'legal') {
    Object.assign(opts, { month: 'long', day: 'numeric', year: 'numeric' });
  } else if (style === 'eu') {
    Object.assign(opts, { day: 'numeric', month: 'short', year: 'numeric' });
  } else {
    Object.assign(opts, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return new Intl.DateTimeFormat(locale, opts).format(date);
}

/**
 * Format time only (no date).
 *
 * @param {string|number|Date|null|undefined} value - ISO string, epoch ms, or Date
 * @param {DateStyle} [style='us'] - 'eu' uses 24h, others use 12h
 * @param {string} [userTimezone] - IANA timezone (defaults to browser)
 * @returns {string} Formatted time string, or '' if input is invalid
 *
 * @example
 *   formatTimeDisplay('2026-03-23T14:30:00.000Z', 'us')  // "2:30 PM"
 *   formatTimeDisplay('2026-03-23T14:30:00.000Z', 'eu')  // "14:30"
 */
export function formatTimeDisplay(value, style, userTimezone) {
  if (style === undefined) style = 'us';
  if (userTimezone === undefined) userTimezone = browserTimezone();

  const date = toDate(value);
  if (!date) return '';

  const locale = style === 'eu' ? 'en-GB' : 'en-US';

  return new Intl.DateTimeFormat(locale, {
    timeZone: userTimezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: style !== 'eu',
  }).format(date);
}

/**
 * Get a human-friendly relative time string (e.g., "2 hours ago", "in 3 days").
 *
 * @param {string|number|Date|null|undefined} value - ISO string, epoch ms, or Date
 * @returns {string} Relative time string, or '' if input is invalid
 *
 * @example
 *   timeAgo('2026-03-23T14:00:00.000Z') // "2 hours ago"
 *   timeAgo(Date.now() - 60000)          // "1 minute ago"
 */
export function timeAgo(value) {
  const date = toDate(value);
  if (!date) return '';

  var now = Date.now();
  var diffMs = now - date.getTime();
  var diffSec = Math.floor(Math.abs(diffMs) / 1000);
  var past = diffMs >= 0;

  if (diffSec < 60) return past ? 'just now' : 'in a moment';

  var units = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];

  for (var i = 0; i < units.length; i++) {
    var unit = units[i][0];
    var seconds = units[i][1];
    var val = Math.floor(diffSec / seconds);
    if (val >= 1) {
      try {
        return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
          past ? -val : val,
          unit
        );
      } catch (e) {
        // Fallback if Intl.RelativeTimeFormat is not available
        return past ? val + ' ' + unit + (val > 1 ? 's' : '') + ' ago' : 'in ' + val + ' ' + unit + (val > 1 ? 's' : '');
      }
    }
  }

  return 'just now';
}

/**
 * Format a short date+time for compact UI contexts (chat messages, lists, etc).
 * Shows time only for today, day+time for this week, date+time otherwise.
 *
 * @param {string|number|Date|null|undefined} value - ISO string, epoch ms, or Date
 * @param {string} [userTimezone] - IANA timezone (defaults to browser)
 * @returns {string} Smart-formatted date/time string, or '' if input is invalid
 *
 * @example
 *   // If today is Mar 23, 2026:
 *   formatSmartDateTime('2026-03-23T14:30:00Z')  // "2:30 PM"
 *   formatSmartDateTime('2026-03-21T09:00:00Z')   // "Fri, 9:00 AM"
 *   formatSmartDateTime('2026-02-15T14:30:00Z')   // "Feb 15, 2:30 PM"
 *   formatSmartDateTime('2025-12-25T14:30:00Z')   // "Dec 25, 2025, 2:30 PM"
 */
export function formatSmartDateTime(value, userTimezone) {
  if (userTimezone === undefined) userTimezone = browserTimezone();

  const date = toDate(value);
  if (!date) return '';

  const now = new Date();
  const isToday =
    date.toLocaleDateString('en-US', { timeZone: userTimezone }) ===
    now.toLocaleDateString('en-US', { timeZone: userTimezone });

  if (isToday) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }

  // Check if same week (within 7 days)
  var diffDays = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
  if (diffDays < 7) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }

  // Check if same year
  var dateYear = new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    year: 'numeric',
  }).format(date);
  var nowYear = new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    year: 'numeric',
  }).format(now);

  if (dateYear === nowYear) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }

  // Different year
  return new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}
